import { locationService } from '@/services/location.service';
import { Pagination } from '@/types/pagination';
import { FilterCriteria, GetAllRequest, SortCriteria } from '@/types/search';
import { extractPagination } from '@/utils/helper';
import { genSearchFilterFor } from '@/utils/search';
import { useMemo, useState } from 'react';
import { Location } from '@/types/location';
import { readPersistedListState } from '@/hooks/useListPersistedState';

export const useTeeLocations = ({
  initialPageSize = 10,
  persistKey,
}: { initialPageSize?: number; persistKey?: string } = {}) => {
  // Read the persisted list-state snapshot exactly once per hook instance —
  // used to seed the four lazy useState initializers below. Only computed
  // when a `persistKey` is supplied; the eight non-list `useTeeLocations()`
  // consumers never read storage at all.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restored = useMemo(
    () => (persistKey ? readPersistedListState(persistKey) : null),
    []
  );

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(restored?.searchTerm ?? '');
  const [filters, setFilters] = useState<Record<string, FilterCriteria>>(
    restored?.filters ?? {}
  );
  const [sort, setSort] = useState<SortCriteria[]>(
    restored?.sort ?? [{ field: 'name', order: 'asc' }]
  );
  const [locations, setLocations] = useState<Location[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: restored?.pagination?.page ?? 1,
    pageSize: restored?.pagination?.pageSize ?? initialPageSize,
    total: 0,
    totalPages: 0,
  });

  const getAllLocations = async (page?: number, sortOverride?: SortCriteria[]) => {
    try {
      setLoading(true);
      const request = {} as GetAllRequest;
      if (searchTerm || Object.keys(filters).length > 0) {
        request.filters = {
          ...filters,
          ...genSearchFilterFor(searchTerm, ['name', 'lId']),
        };
      }
      request.pagination = {
        page: page ?? pagination.page,
        pageSize: pagination.pageSize,
      };
      request.sort = sortOverride ?? sort;
      const response = await locationService.getAllLocations(request);
      const locations = response.data || [];
      setLocations(locations);
      setPagination(extractPagination(response));
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    locations,
    loading,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    sort,
    setSort,
    getAllLocations,
    pagination,
    setPagination,
  };
};
